import React, { memo, MutableRefObject, useEffect, useRef } from "react";
import { Vector3 } from "@react-three/fiber";
import { Vector3 as ThreeVector3 } from "three";
import shallow from "zustand/shallow";
import { Beam, Rack, Shelves } from "../items";
import { DimensionSectionHorizontal } from "../DimensionSectionHorizontal";
import { useColors } from "../../../hooks/useColors";
import { usePosition } from "../../../hooks/usePosition";
import { generateID } from "../../../store/helpers/generateID";
import { SectionInterface, SideTypeWithoutTop } from "../../../store/types/store";
import convertDegreesToRadians from "../../../utils/convertDegreesToRadians";
import { Group } from "three/src/objects/Group";
import { TotalDimensionHorizontal } from "../TotalDimensionHorizontal";
import { TotalDimensionVertical } from "../TotalDimensionVertical";
import { TotalDimensionRoomHorizontal } from "../TotalDimensionRoomHorizontal";
import { TotalDimensionRoomVertical } from "../TotalDimensionRoomVertical";
import { OffsetCeilingDimensionVertical } from "../OffsetCeilingDimensionVertical";
import { useShelvingStore } from "../../../store/useShelvingStore";
import { useRoomSizes } from "../../../hooks";
import { useStoreSystem } from "../../../store";
import { SectionItemI } from "../../../shared";

interface RacksProps {
    moveCameraEnabled: MutableRefObject<boolean>;
    side: SideTypeWithoutTop;
}

export const Racks = ({ moveCameraEnabled, side }: RacksProps) => {
    const groupRef = useRef<Group | null>(null);
    const { colorsShade, colorsItems } = useColors();
    const { roomLength, roomDepth } = useRoomSizes();
    const { beams, updateBeams, updateNomenklature, sections } = useShelvingStore(
        (state) => ({
            beams: state.shelving[side].beams,
            updateBeams: state.updateBeams,
            updateNomenklature: state.updateNomenklature,
            sections: state.shelving[side].sections,
        }),
        shallow
    );

    const { currentSide } = useStoreSystem(
        (state) => ({
            currentSide: state.currentSide,
        }),
        shallow
    );

    const {
        beamPosition,
        dimensionPosition,
        firstRackPosition,
        leftBracketShelfPosition,
        leftRackInSectionPositions,
        rightBracketShelfPosition,
        rightRackInSectionPositions,
        oneRackInSectionPositions,
        shelfPosition,
        partPosition,
        startXPosition,
    } = usePosition(side);

    useEffect(() => {
        updateBeams(side);
        updateNomenklature(side);
    }, [sections]);

    useEffect(() => {
        const group = groupRef.current;

        if (group && side === "left") {
            const leftRacksStart = new ThreeVector3(-roomLength / 2000, 0, roomDepth / 2000);

            const { x, z } = group.worldToLocal(leftRacksStart);
            group.translateZ(z);
            group.translateX(x);
            group.rotation.set(0, convertDegreesToRadians(90), 0);
        }

        if (group && side === "right") {
            const leftRacksStart = new ThreeVector3(roomLength / 2000, 0, roomDepth / 2000);

            const { x, z } = group.worldToLocal(leftRacksStart);
            group.translateZ(z);
            group.translateX(x);
            group.rotation.set(0, convertDegreesToRadians(270), 0);
        }

        if (group && side === "back") {
            const leftRacksStart = new ThreeVector3(0, 0, roomDepth / 1000);

            const { x, z } = group.worldToLocal(leftRacksStart);
            group.translateZ(z);
            group.translateX(x);
            group.rotation.set(0, convertDegreesToRadians(180), 0);
        }
    }, [groupRef, roomLength, roomDepth, sections]);

    const leftRacksStartVector = new ThreeVector3(-roomLength / 2000, 0, roomDepth / 2000);

    const leftRacksStart = [leftRacksStartVector.x, leftRacksStartVector.y, leftRacksStartVector.z];

    return (
        <group name={side + "Racks"} ref={groupRef}>
            {side === currentSide && (
                <>
                    <TotalDimensionHorizontal />
                    <TotalDimensionVertical />
                    <TotalDimensionRoomHorizontal />
                    <TotalDimensionRoomVertical />
                    <OffsetCeilingDimensionVertical />
                </>
            )}
            {sections.length > 0 && !sections[0].isIndent && (
                <Rack
                    width={sections[0].width}
                    height={sections[0].leftRackHeight}
                    isIndent={sections[0].isIndent}
                    offset={sections[0].offset}
                    sectionId={sections[0].id}
                    position={firstRackPosition}
                    isFirstRack
                />
            )}
            {sections.map((section, index) => {
                if (sections[index - 1] && sections[index - 1].isIndent && !section.isIndent) {
                    return (
                        <group key={index}>
                            <Rack
                                width={section.width}
                                height={section.leftRackHeight}
                                isIndent={section.isIndent}
                                offset={section.offset}
                                sectionId={section.id}
                                position={leftRackInSectionPositions[index]}
                                isFirstRack
                            />

                            <Rack
                                width={section.width}
                                height={section.height}
                                isIndent={section.isIndent}
                                offset={section.offset}
                                sectionId={section.id}
                                position={rightRackInSectionPositions[index]}
                            />
                            {side === currentSide && (
                                <DimensionSectionHorizontal
                                    position={dimensionPosition[index]}
                                    isIndent={false}
                                    width={section.width}
                                />
                            )}
                        </group>
                    );
                }
                if (section.isIndent && side === currentSide) {
                    return (
                        <DimensionSectionHorizontal
                            key={index}
                            position={dimensionPosition[index]}
                            isIndent={section.isIndent}
                            width={section.width}
                        />
                    );
                }

                return (
                    <group key={index}>
                        <Rack
                            sectionId={section.id}
                            width={section.width}
                            height={section.height}
                            isIndent={section.isIndent}
                            offset={section.offset}
                            position={oneRackInSectionPositions[index]}
                        />
                        {side === currentSide && (
                            <DimensionSectionHorizontal
                                position={dimensionPosition[index]}
                                isIndent={section.isIndent}
                                width={section.width}
                            />
                        )}
                    </group>
                );
            })}
            {sections.map((section: SectionInterface, index: number) => {
                if (section.isIndent) {
                    return null;
                }
                return (
                    <group key={index}>
                        {section.items.map((shelf: SectionItemI, i: number) => {
                            if (!shelf.model) return null;
                            return (
                                <Shelves
                                    model={shelf.model}
                                    shelfName={shelf.name}
                                    itemId={shelf.id}
                                    newSectionId={shelf.sectionID}
                                    shelf={shelf}
                                    sectionId={section.id}
                                    key={shelf.id}
                                    brackets={shelf.brackets}
                                    parts={shelf.parts}
                                    isSpirit={shelf.isSpirit}
                                    leftRacksStart={leftRacksStart}
                                    bracketsPosition={{
                                        left: leftBracketShelfPosition[index][i],
                                        right: rightBracketShelfPosition[index][i],
                                    }}
                                    partPosition={partPosition[index][i]}
                                    position={shelfPosition[index][i] as (Vector3 | undefined) & number[]}
                                    startXPosition={startXPosition}
                                    side={side}
                                />
                            );
                        })}

                        {beams?.map((beam, index) => {
                            return (
                                <Beam
                                    key={generateID()}
                                    model={beam.model}
                                    color={colorsShade[colorsItems.guides]}
                                    position={beamPosition[index] as Vector3}
                                />
                            );
                        })}
                    </group>
                );
            })}
        </group>
    );
};
